import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import './SmartSearch.css';

const CATEGORIES = [
  { key: 'all',        label: 'Todos os ativos',     icon: '⊞' },
  { key: 'scanner',    label: 'Scanner B3',          icon: '◉' },
  { key: 'rsi2',       label: 'RSI(2) Connors',      icon: '⟳' },
  { key: 'confluence', label: 'Kairi + Confluência', icon: '◈' },
  { key: 'watch',      label: 'Watch (qualquer)',    icon: '◎' },
  { key: 'filtered',   label: 'Filtrados MM200',     icon: '✕' },
];

function scannerSig(signal) {
  if (signal === 'buy')         return { label: 'Compra',    cls: 'ss-badge-scanner-buy' };
  if (signal === 'investigate') return { label: 'Investig.', cls: 'ss-badge-scanner-inv' };
  if (signal === 'danger')      return { label: 'Cuidado',   cls: 'ss-badge-scanner-danger' };
  if (signal === 'watch')       return { label: 'Watch',     cls: 'ss-badge-scanner-watch' };
  return null;
}
function rsi2Sig(status) {
  if (status === 'buy')         return { label: 'COMPRAR',  cls: 'ss-badge-rsi2-buy' };
  if (status === 'approaching') return { label: 'PRÓXIMO',  cls: 'ss-badge-rsi2-app' };
  return null;
}
function confSig(signal, score) {
  if (signal === 'buy')   return { label: `${score ?? '—'}%`, cls: 'ss-badge-conf-buy' };
  if (signal === 'watch') return { label: `${score ?? '—'}%`, cls: 'ss-badge-conf-watch' };
  return null;
}

export default function SmartSearch({ assets = [], onSelect }) {
  const [open, setOpen]         = useState(false);
  const [query, setQuery]       = useState('');
  const [category, setCategory] = useState('all');
  const [activeIdx, setActiveIdx] = useState(0);

  const rootRef  = useRef(null);
  const inputRef = useRef(null);
  const optionsIntent = /\s+OPÇÕES?$/i.test(query.trim());
  const assetQuery = query.trim().replace(/\s+OPÇÕES?$/i, '').trim();

  // ⌘K / Ctrl+K shortcut
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
      if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Click outside
  useEffect(() => {
    function onClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Foco na abertura, sem atraso: o que for digitado logo após o clique cai no campo
  useLayoutEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  // Counts por setup
  const counts = useMemo(() => ({
    all:        assets.length,
    scanner:    assets.filter(a => a.signal === 'buy' || a.signal === 'investigate').length,
    rsi2:       assets.filter(a => a.rsi2Status === 'buy').length,
    confluence: assets.filter(a => a.confluenceSignal === 'buy').length,
    watch:      assets.filter(a => a.signal === 'watch' || a.rsi2Status === 'approaching' || a.confluenceSignal === 'watch').length,
    filtered:   assets.filter(a => a.diagnosis === 'filtered').length,
  }), [assets]);

  // Total de oportunidades únicas (pelo menos 1 sinal de compra em qualquer setup)
  const hasOpportunity = useMemo(() =>
    assets.filter(a =>
      a.signal === 'buy' || a.signal === 'investigate' ||
      a.rsi2Status === 'buy' ||
      a.confluenceSignal === 'buy'
    ).length
  , [assets]);

  const filtered = useMemo(() => {
    let list = assets;
    if (category === 'scanner')    list = list.filter(a => a.signal === 'buy' || a.signal === 'investigate');
    else if (category === 'rsi2')       list = list.filter(a => a.rsi2Status === 'buy');
    else if (category === 'confluence') list = list.filter(a => a.confluenceSignal === 'buy');
    else if (category === 'watch')      list = list.filter(a => a.signal === 'watch' || a.rsi2Status === 'approaching' || a.confluenceSignal === 'watch');
    else if (category === 'filtered')   list = list.filter(a => a.diagnosis === 'filtered');

    if (assetQuery) {
      const q = assetQuery.toUpperCase();
      list = list.filter(a =>
        a.ticker?.toUpperCase().includes(q) ||
        a.name?.toUpperCase().includes(q) ||
        a.sector?.toUpperCase().includes(q)
      );
    }
    // Sort: ticker digitado primeiro (exato, depois prefixo); depois ativos com mais sinais
    const q = assetQuery.toUpperCase();
    const rank = a => (!q ? 0 : a.ticker?.toUpperCase() === q ? 2 : a.ticker?.toUpperCase().startsWith(q) ? 1 : 0);
    return [...list].sort((a, b) => {
      if (rank(a) !== rank(b)) return rank(b) - rank(a);
      const scoreA = [a.signal === 'buy' || a.signal === 'investigate', a.rsi2Status === 'buy', a.confluenceSignal === 'buy'].filter(Boolean).length;
      const scoreB = [b.signal === 'buy' || b.signal === 'investigate', b.rsi2Status === 'buy', b.confluenceSignal === 'buy'].filter(Boolean).length;
      return scoreB - scoreA;
    }).slice(0, 40);
  }, [assets, assetQuery, category]);

  useEffect(() => setActiveIdx(0), [filtered]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && filtered[activeIdx]) {
      if (optionsIntent) window.location.href = `/opcoes/${encodeURIComponent(filtered[activeIdx].ticker)}`;
      else handleSelect(filtered[activeIdx]);
    }
  };

  const handleSelect = (asset) => {
    setOpen(false);
    setQuery('');
    if (onSelect) onSelect(asset);
  };

  return (
    <div className="ss-root" ref={rootRef}>
      {/* ── Trigger ── */}
      <button
        className={`ss-trigger ${open ? 'ss-trigger--open' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <svg className="ss-trigger-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3">
          <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
        </svg>
        <span className="ss-trigger-text">Buscar ativo...</span>
        {hasOpportunity > 0 && (
          <span className="ss-trigger-badge">{hasOpportunity} setup{hasOpportunity > 1 ? 's' : ''}</span>
        )}
        <kbd className="ss-kbd">⌘K</kbd>
      </button>

      {/* ── Dropdown overlay ── */}
      {open && (
        <div className="ss-overlay">
          <div className="ss-dropdown">

            {/* Input */}
            <div className="ss-input-wrap">
              <svg className="ss-input-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
              </svg>
              <input
                ref={inputRef}
                autoFocus
                className="ss-input"
                type="text"
                placeholder="Buscar por ticker, nome ou setor..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                autoComplete="off"
                spellCheck={false}
              />
              <button className="ss-esc-btn" onClick={() => { setOpen(false); setQuery(''); }}>ESC</button>
            </div>

            <div className="ss-body">
              {/* ── Categories ── */}
              <div className="ss-cats">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.key}
                    className={`ss-cat ${category === cat.key ? 'ss-cat--active' : ''}`}
                    onClick={() => setCategory(cat.key)}
                  >
                    <span className="ss-cat-icon">{cat.icon}</span>
                    <span className="ss-cat-label">{cat.label}</span>
                    {counts[cat.key] > 0 && (
                      <span className={`ss-cat-count ${
                        cat.key === 'scanner' ? 'scanner' :
                        cat.key === 'rsi2' ? 'rsi2' :
                        cat.key === 'confluence' ? 'conf' : ''
                      }`}>
                        {counts[cat.key]}
                      </span>
                    )}
                  </button>
                ))}

                {/* Legenda dos setups */}
                <div className="ss-cats-divider" />
                <div className="ss-cats-legend">
                  <div className="ss-legend-item">
                    <span className="ss-legend-dot scanner" />Scanner B3
                  </div>
                  <div className="ss-legend-item">
                    <span className="ss-legend-dot rsi2" />RSI(2) Connors
                  </div>
                  <div className="ss-legend-item">
                    <span className="ss-legend-dot conf" />Kairi+Confl.
                  </div>
                </div>

                {hasOpportunity > 0 && (
                  <div className="ss-cats-hint">
                    <span className="ss-cats-hint-dot" />
                    {hasOpportunity} oportunidade{hasOpportunity > 1 ? 's' : ''} hoje
                  </div>
                )}
              </div>

              {/* ── Results ── */}
              <div className="ss-results-wrap">
                {assets.length === 0 ? (
                  <div className="ss-empty">
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" opacity="0.25">
                      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                    </svg>
                    <div className="ss-empty-title">Carregando ativos...</div>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="ss-empty">
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" opacity="0.25">
                      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                    </svg>
                    <div className="ss-empty-title">Nenhum ativo encontrado</div>
                    <div className="ss-empty-sub">Tente outro ticker ou setor</div>
                  </div>
                ) : (
                  <div className="ss-results">
                    {filtered.map((a, i) => {
                      const sc = scannerSig(a.signal);
                      const rc = rsi2Sig(a.rsi2Status);
                      const cc = confSig(a.confluenceSignal, a.confluenceScore);
                      const anySignal = sc || rc || cc;

                      return (
                        <div
                          key={a.ticker}
                          className={`ss-result ${i === activeIdx ? 'ss-result--active' : ''} ${anySignal ? 'ss-result--has-signal' : ''}`}
                          onClick={() => handleSelect(a)}
                          onMouseEnter={() => setActiveIdx(i)}
                        >
                          {/* Left: ticker + name */}
                          <div className="ss-result-main">
                            <div className="ss-result-top">
                              <span className="ss-ticker">{a.ticker}</span>
                              {a.kairi != null && (
                                <span className={`ss-kairi-val ${a.kairi <= -5 ? 'neg' : a.kairi >= 5 ? 'pos' : 'neutral'}`}>
                                  {a.kairi > 0 ? '+' : ''}{a.kairi.toFixed(1)}%
                                </span>
                              )}
                              {a.zKairi != null && (
                                <span className={`ss-z-val ${a.zKairi <= -1.5 ? 'neg' : a.zKairi > 0 ? 'pos' : 'neutral'}`}>
                                  {a.zKairi > 0 ? '+' : ''}{a.zKairi.toFixed(2)}σ
                                </span>
                              )}
                            </div>
                            <div className="ss-result-sub">
                              <span className="ss-name">{a.name || '—'}</span>
                              {a.sector && <span className="ss-sector">{a.sector}</span>}
                            </div>
                          </div>

                          {/* Right: setup badges */}
                          <div className="ss-setup-badges">
                            {optionsIntent && (
                              <button
                                type="button"
                                className="ss-badge"
                                onClick={event => {
                                  event.stopPropagation();
                                  window.location.href = `/opcoes/${encodeURIComponent(a.ticker)}`;
                                }}
                              >
                                Grade de opções
                              </button>
                            )}
                            {sc && (
                              <span className={`ss-badge ${sc.cls}`}>
                                <span className="ss-badge-prefix">Scanner</span>
                                {sc.label}
                              </span>
                            )}
                            {rc && (
                              <span className={`ss-badge ${rc.cls}`}>
                                <span className="ss-badge-prefix">RSI(2)</span>
                                {rc.label}
                              </span>
                            )}
                            {cc && (
                              <span className={`ss-badge ${cc.cls}`}>
                                <span className="ss-badge-prefix">Conf.</span>
                                {cc.label}
                              </span>
                            )}
                            {!anySignal && a.diagnosis === 'filtered' && (
                              <span className="ss-badge ss-badge-filtered">MM200 ✕</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="ss-footer">
              <span className="ss-foot-key">↑↓ navegar</span>
              <span className="ss-foot-key">↵ selecionar</span>
              <span className="ss-foot-key">ESC fechar</span>
              <span className="ss-foot-total">{filtered.length} de {assets.length} ativos</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
